import { css } from 'lit';

export const styles = css`
  :host {
    display: block;
    min-height: calc(100vh - var(--app-header-height));
  }

  .auth-shell {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: clamp(1rem, 3vw, 2.5rem);
    align-items: stretch;
    max-width: 1180px;
    margin: 0 auto;
  }

  @media (min-width: 900px) {
    .auth-shell {
      grid-template-columns: 1.05fr 1fr;
      gap: 3rem;
      align-items: center;
      min-height: calc(100vh - var(--app-header-height) - 6rem);
    }
  }

  /* ---------- Brand / marketing pane ---------- */

  .brand-pane {
    position: relative;
    border-radius: var(--sl-border-radius-x-large);
    padding: clamp(1.25rem, 3vw, 2.5rem);
    background: linear-gradient(
      135deg,
      rgba(225, 71, 126, 0.08),
      rgba(255, 184, 108, 0.06)
    );
    border: 1px solid var(--app-border);
    overflow: hidden;
  }

  .brand-pane::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--app-gradient-hero);
    pointer-events: none;
  }

  .brand-pane > * {
    position: relative;
  }

  .brand-mark {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .brand-mark .brand-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(15, 42, 68, 0.2);
  }

  .brand-mark .brand-name {
    font-family: var(--font-display, var(--sl-font-sans));
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1;
    color: #0f2a44;
  }

  .brand-mark .brand-name-accent {
    color: #f4b942;
  }

  @media (prefers-color-scheme: dark) {
    .brand-mark .brand-name {
      color: #e8edf2;
    }
  }

  .brand-headline {
    font-size: clamp(1.65rem, 2.4vw, 2.1rem);
    line-height: 1.15;
    font-weight: 700;
    margin: 0 0 0.75rem;
    letter-spacing: -0.02em;
  }

  .brand-sub {
    font-size: 1rem;
    line-height: 1.55;
    color: var(--app-text-muted);
    margin: 0 0 1.5rem;
    max-width: 38ch;
  }

  .brand-features {
    list-style: none;
    padding: 0;
    margin: 0 0 1rem;
    display: grid;
    gap: 0.6rem;
  }

  .brand-features li {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    font-size: 0.95rem;
    color: var(--app-text);
  }

  .brand-features sl-icon {
    color: var(--brand-rose-500);
    margin-top: 0.15rem;
    flex-shrink: 0;
  }

  /* ---------- Auth card pane ---------- */

  .auth-card {
    display: block;
    width: 100%;
    max-width: 460px;
    margin: 0 auto;
  }

  .auth-card::part(base) {
    border-radius: var(--sl-border-radius-x-large);
    box-shadow: var(--app-shadow-lg);
    border: 1px solid var(--app-border);
    background: var(--app-surface);
  }

  .auth-card::part(body) {
    padding: 1.75rem;
  }

  @media (min-width: 480px) {
    .auth-card::part(body) {
      padding: 2.25rem;
    }
  }

  .auth-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.25rem;
    letter-spacing: -0.015em;
  }

  .auth-subtitle {
    margin: 0 0 1.25rem;
    color: var(--app-text-muted);
    font-size: 0.95rem;
  }

  /* Segmented control for sign-in / register */
  .auth-mode-switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
    background: var(--sl-color-neutral-100);
    padding: 4px;
    border-radius: var(--sl-border-radius-large);
    margin-bottom: 1.25rem;
    position: relative;
  }

  @media (prefers-color-scheme: dark) {
    .auth-mode-switch {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  .auth-mode-switch button {
    appearance: none;
    border: none;
    background: transparent;
    padding: 0.5rem 0.75rem;
    border-radius: calc(var(--sl-border-radius-large) - 4px);
    font: inherit;
    font-weight: 500;
    color: var(--app-text-muted);
    cursor: pointer;
    transition:
      color 120ms ease,
      background-color 120ms ease,
      box-shadow 120ms ease;
  }

  .auth-mode-switch button.active {
    background: var(--app-surface);
    color: var(--app-text);
    box-shadow: var(--app-shadow-sm);
  }

  .auth-mode-switch button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .auth-form {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .auth-form sl-input::part(form-control-label) {
    font-size: 0.85rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  }

  .auth-form sl-input::part(base) {
    border-radius: var(--sl-border-radius-medium);
  }

  .auth-form sl-button[type='submit']::part(base) {
    border-radius: var(--sl-border-radius-medium);
    font-weight: 600;
    height: 2.75rem;
  }

  .auth-form sl-button[type='submit'] {
    margin-top: 0.5rem;
  }

  .auth-hint {
    font-size: 0.85rem;
    color: var(--app-text-muted);
    margin: 0;
    line-height: 1.45;
  }

  .auth-error::part(base) {
    border-radius: var(--sl-border-radius-medium);
  }

  /* "or continue with" divider */
  .or-divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.5rem 0 1rem;
    color: var(--app-text-muted);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .or-divider::before,
  .or-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--app-border);
  }

  /* Social login buttons */
  .login-buttons {
    display: grid;
    gap: 0.6rem;
  }

  .login-button::part(base) {
    width: 100%;
    border-radius: var(--sl-border-radius-medium);
    font-weight: 500;
    height: 2.75rem;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    color: var(--app-text);
    transition:
      border-color 120ms ease,
      background-color 120ms ease;
  }

  .login-button::part(base):hover {
    background: var(--sl-color-neutral-50);
  }

  @media (prefers-color-scheme: dark) {
    .login-button::part(base):hover {
      background: rgba(255, 255, 255, 0.04);
    }
  }

  .login-button .provider-icon {
    font-size: 1.1rem;
  }

  .provider-icon.facebook {
    color: #1877f2;
  }

  .provider-icon.google {
    color: #ea4335;
  }

  /* About / language tabs below the auth card */
  /* Legal links (Privacy / Terms) — discoverable from any
     anonymous viewer of the login page; also satisfies Google's
     OAuth consent screen requirement that the privacy URL be
     reachable from the app's homepage. */
  .legal-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.6rem;
    padding: 1rem 1rem 2rem;
    font-family: var(
      --font-mono,
      ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace
    );
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--app-text-muted, var(--sl-color-neutral-600));
  }
  .legal-footer a {
    color: inherit;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.15s ease;
  }
  .legal-footer a:hover,
  .legal-footer a:focus-visible {
    border-bottom-color: currentColor;
  }
`;
